if (!customElements.get('turf-calculator')) {
  customElements.define(
    'turf-calculator',
    class TurfCalculator extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-turf-calculator-form]');
        if (!this.form) return;

        this.sectionId = this.dataset.sectionId || 'turf-calculator';
        this.areasContainer = this.querySelector('[data-turf-areas]');
        this.areaTemplate = this.querySelector('template[data-turf-area-template]');
        this.addAreaButton = this.querySelector('[data-turf-add-area]');
        this.resetButton = this.querySelector('[data-turf-reset]');
        this.calculateButton = this.querySelector('[data-turf-calculate]');
        this.depthInput = this.querySelector('[data-turf-depth]');
        this.wastageCheckbox = this.querySelector('[data-turf-wastage-enabled]');
        this.wastageInput = this.querySelector('[data-turf-wastage-percent]');
        this.error = this.querySelector('[data-turf-error]');
        this.externalResults = Array.from(this.querySelectorAll('[data-turf-result-external]'));
        this.fallbackResult = this.querySelector('[data-turf-result-fallback]');

        this.maxAreas = this.toPositiveNumber(this.form.dataset.maxAreas) || 10;
        this.defaultDepth = this.toPositiveNumber(this.form.dataset.defaultDepth) || 100;
        this.defaultWastage = this.toNonNegativeNumber(this.form.dataset.defaultWastage);
        this.turfRoundTo = this.toPositiveNumber(this.form.dataset.turfRoundTo) || 1;
        this.underturfRoundTo = this.toPositiveNumber(this.form.dataset.underturfRoundTo) || (1 / 3);
        this.liveCalculation = this.form.dataset.liveCalculation === 'true';
        this.defaultWastageEnabled = this.form.dataset.wastageEnabled === 'true';

        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleInput = this.handleInput.bind(this);

        this.form.addEventListener('submit', this.handleFormSubmit);
        this.form.addEventListener('input', this.handleInput);
        this.form.addEventListener('change', this.handleInput);

        this.addAreaButton?.addEventListener('click', () => this.addArea());
        this.resetButton?.addEventListener('click', () => this.reset());
        this.areasContainer?.addEventListener('click', (event) => {
          const removeButton = event.target.closest('[data-turf-remove-area]');
          if (!removeButton) return;
          this.removeArea(removeButton.closest('[data-turf-area]'));
        });

        this.syncAreaUI();
        this.updateWastageUI();
        this.setResultsEmpty();
        this.setupResponsiveBlockOrder();
      }

      disconnectedCallback() {
        if (!this.mobileOrderQuery || !this.handleResponsiveBlockOrder) return;

        if (this.mobileOrderQuery.removeEventListener) {
          this.mobileOrderQuery.removeEventListener('change', this.handleResponsiveBlockOrder);
        } else if (this.mobileOrderQuery.removeListener) {
          this.mobileOrderQuery.removeListener(this.handleResponsiveBlockOrder);
        }
      }

      setupResponsiveBlockOrder() {
        this.columnsContainer = this.querySelector('.turf-calculator-section__columns');
        if (!this.columnsContainer) return;

        const columnElements = Array.from(
          this.columnsContainer.querySelectorAll('.turf-calculator-section__column')
        );

        this.responsiveBlockOrigins = [];
        let sequence = 0;

        columnElements.forEach((column) => {
          Array.from(column.children).forEach((block, index) => {
            if (!block.classList.contains('turf-calculator-block')) return;

            this.responsiveBlockOrigins.push({
              block,
              parent: column,
              index,
              sequence: sequence++,
            });
          });
        });

        if (!this.responsiveBlockOrigins.length) return;

        this.mobileOrderQuery = window.matchMedia('(max-width: 749px)');
        this.handleResponsiveBlockOrder = () => this.applyResponsiveBlockOrder();

        if (this.mobileOrderQuery.addEventListener) {
          this.mobileOrderQuery.addEventListener('change', this.handleResponsiveBlockOrder);
        } else if (this.mobileOrderQuery.addListener) {
          this.mobileOrderQuery.addListener(this.handleResponsiveBlockOrder);
        }

        this.applyResponsiveBlockOrder();
      }

      applyResponsiveBlockOrder() {
        if (!this.columnsContainer || !this.responsiveBlockOrigins?.length) return;

        if (this.mobileOrderQuery.matches) {
          const priority = {
            calculator: 10,
            result: 20,
            image_text: 30,
            checklist: 40,
            info_box: 50,
            icon_text: 50,
            rich_text: 50,
          };

          const orderedBlocks = [...this.responsiveBlockOrigins].sort((a, b) => {
            const aPriority = priority[a.block.dataset.turfBlock] ?? 60;
            const bPriority = priority[b.block.dataset.turfBlock] ?? 60;

            return aPriority - bPriority || a.sequence - b.sequence;
          });

          orderedBlocks.forEach(({ block }) => this.columnsContainer.appendChild(block));
          this.columnsContainer.classList.add('turf-calculator-section__columns--mobile-reordered');
          return;
        }

        const grouped = new Map();

        this.responsiveBlockOrigins.forEach((origin) => {
          if (!grouped.has(origin.parent)) grouped.set(origin.parent, []);
          grouped.get(origin.parent).push(origin);
        });

        grouped.forEach((origins, parent) => {
          origins
            .sort((a, b) => a.index - b.index)
            .forEach(({ block }) => parent.appendChild(block));
        });

        this.columnsContainer.classList.remove('turf-calculator-section__columns--mobile-reordered');
      }

      handleFormSubmit(event) {
        event.preventDefault();
        this.calculate();
      }

      handleInput(event) {
        this.hideError();

        if (
          event.target.matches('[data-turf-wastage-enabled], [data-turf-wastage-percent]')
        ) {
          this.updateWastageUI();
        }

        if (this.liveCalculation) {
          this.calculate({ silent: true });
        }
      }

      toPositiveNumber(value) {
        const number = parseFloat(value);
        return Number.isFinite(number) && number > 0 ? number : 0;
      }

      toNonNegativeNumber(value) {
        const number = parseFloat(value);
        return Number.isFinite(number) && number >= 0 ? number : 0;
      }

      formatNumber(value, decimals = 2) {
        return Number(value)
          .toFixed(decimals)
          .replace(/\.?0+$/, '');
      }

      roundUp(value, increment) {
        if (!increment || increment <= 0) return value;
        return Math.ceil((value / increment) - 0.0000001) * increment;
      }

      getAreas() {
        return Array.from(this.querySelectorAll('[data-turf-area]'));
      }

      addArea() {
        const areas = this.getAreas();

        if (!this.areaTemplate || areas.length >= this.maxAreas) {
          this.showError(`You can add up to ${this.maxAreas} lawn areas.`);
          return;
        }

        const fragment = this.areaTemplate.content.cloneNode(true);
        const area = fragment.querySelector('[data-turf-area]');

        this.areasContainer.appendChild(fragment);
        this.prepareArea(area, this.getAreas().length);
        this.syncAreaUI();

        area?.querySelector('[data-area-length]')?.focus();
      }

      removeArea(area) {
        if (!area || this.getAreas().length <= 1) return;

        area.remove();
        this.renumberAreas();
        this.syncAreaUI();

        if (this.liveCalculation) this.calculate({ silent: true });
      }

      prepareArea(area, index) {
        if (!area) return;

        const lengthInput = area.querySelector('[data-area-length]');
        const widthInput = area.querySelector('[data-area-width]');
        const lengthLabel = area.querySelector('[data-area-length-label]');
        const widthLabel = area.querySelector('[data-area-width-label]');
        const areaLabel = area.querySelector('[data-area-label]');

        const lengthId = `${this.sectionId}-turf-length-${index}`;
        const widthId = `${this.sectionId}-turf-width-${index}`;

        if (lengthInput) lengthInput.id = lengthId;
        if (widthInput) widthInput.id = widthId;
        if (lengthLabel) lengthLabel.htmlFor = lengthId;
        if (widthLabel) widthLabel.htmlFor = widthId;
        if (areaLabel) areaLabel.textContent = `Area ${index}`;

        area.dataset.areaIndex = String(index);
      }

      renumberAreas() {
        this.getAreas().forEach((area, index) => this.prepareArea(area, index + 1));
      }

      syncAreaUI() {
        const areas = this.getAreas();
        const multiple = areas.length > 1;

        this.classList.toggle('turf-calculator--multiple-areas', multiple);

        areas.forEach((area) => {
          const removeButton = area.querySelector('[data-turf-remove-area]');
          if (removeButton) removeButton.hidden = !multiple;
        });

        if (this.addAreaButton) {
          const atLimit = areas.length >= this.maxAreas;
          this.addAreaButton.disabled = atLimit;
          this.addAreaButton.setAttribute('aria-disabled', String(atLimit));
        }
      }

      updateWastageUI() {
        const enabled = this.wastageCheckbox ? this.wastageCheckbox.checked : false;
        const editableWrap = this.querySelector('[data-turf-wastage-editable]');

        if (editableWrap) {
          editableWrap.classList.toggle('is-disabled', !enabled);
        }

        if (this.wastageInput) {
          this.wastageInput.disabled = !enabled;
        }
      }

      readAreas({ silent = false } = {}) {
        const completeAreas = [];
        let hasPartialArea = false;

        this.getAreas().forEach((area, index) => {
          const length = this.toPositiveNumber(area.querySelector('[data-area-length]')?.value);
          const width = this.toPositiveNumber(area.querySelector('[data-area-width]')?.value);

          if (!length && !width) return;

          if (!length || !width) {
            hasPartialArea = true;
            return;
          }

          completeAreas.push({
            index: index + 1,
            length,
            width,
            area: length * width,
          });
        });

        if (hasPartialArea) {
          if (!silent) this.showError('Enter both a length and width for each lawn area.');
          return null;
        }

        if (!completeAreas.length) {
          if (!silent) this.showError('Enter at least one lawn area before calculating.');
          return null;
        }

        return completeAreas;
      }

      getWastagePercent() {
        if (!this.wastageCheckbox?.checked) return 0;

        if (this.wastageInput) {
          return this.toNonNegativeNumber(this.wastageInput.value);
        }

        return this.defaultWastage;
      }

      calculate({ silent = false } = {}) {
        this.hideError();

        const areas = this.readAreas({ silent });
        if (!areas) {
          if (!silent) this.setResultsEmpty();
          return null;
        }

        const depthMm = this.toPositiveNumber(this.depthInput?.value);
        if (!depthMm) {
          if (!silent) this.showError('Enter an underturf depth before calculating.');
          return null;
        }

        const measuredArea = areas.reduce((total, item) => total + item.area, 0);
        const wastagePercent = this.getWastagePercent();
        const wastageArea = measuredArea * (wastagePercent / 100);
        const turfArea = measuredArea + wastageArea;
        const turfOrderQuantity = this.roundUp(turfArea, this.turfRoundTo);

        // Underturf is based on the actual prepared lawn area, not the extra turf wastage.
        const underturfCalculated = measuredArea * (depthMm / 1000);
        const underturfRounded = this.roundUp(underturfCalculated, this.underturfRoundTo);

        const isThirdIncrement = Math.abs(this.underturfRoundTo - (1 / 3)) < 0.001;
        let underturfFullQuantity = 0;
        let underturfThirdQuantity = 0;

        if (isThirdIncrement) {
          const totalThirds = Math.ceil((underturfCalculated * 3) - 0.000001);
          underturfFullQuantity = Math.floor(totalThirds / 3);
          underturfThirdQuantity = totalThirds % 3;
        }

        const result = {
          areas,
          areaCount: areas.length,
          measuredArea,
          wastagePercent,
          wastageArea,
          turfArea,
          turfOrderQuantity,
          depthMm,
          underturfCalculated,
          underturfRounded,
          underturfFullQuantity,
          underturfThirdQuantity,
          isThirdIncrement,
        };

        this.renderResult(result);

        this.dispatchEvent(
          new CustomEvent('turf-calculator:calculated', {
            bubbles: true,
            detail: result,
          })
        );

        return result;
      }

      renderResult(result) {
        const panels = this.externalResults.length
          ? this.externalResults
          : this.fallbackResult
            ? [this.fallbackResult]
            : [];

        panels.forEach((panel) => {
          panel.hidden = false;

          panel.querySelector('[data-result-empty]')?.setAttribute('hidden', '');
          panel.querySelector('[data-result-complete]')?.removeAttribute('hidden');

          this.setText(panel, '[data-result-area-count]', `${result.areaCount} ${result.areaCount === 1 ? 'area' : 'areas'}`);
          this.setText(panel, '[data-result-measured-area]', `${this.formatNumber(result.measuredArea)} m²`);
          this.setText(panel, '[data-result-turf]', `${this.formatNumber(result.turfOrderQuantity)} m²`);
          this.setText(panel, '[data-result-depth]', `${this.formatNumber(result.depthMm)} mm`);
          this.setText(panel, '[data-result-underturf-calculated]', `${this.formatNumber(result.underturfCalculated, 3)} m³`);
          this.setText(panel, '[data-result-underturf]', `${this.formatNumber(result.underturfRounded, 3)} m³`);

          const wastageRow = panel.querySelector('[data-result-wastage-row]');
          if (wastageRow) {
            wastageRow.hidden = result.wastagePercent <= 0;
            if (result.wastagePercent > 0) {
              this.setText(
                panel,
                '[data-result-wastage]',
                `${this.formatNumber(result.wastagePercent)}% (+${this.formatNumber(result.wastageArea)} m²)`
              );
            }
          }

          const orderBreakdown = panel.querySelector('[data-result-underturf-order]');
          if (orderBreakdown) {
            if (result.isThirdIncrement) {
              const parts = [];

              if (result.underturfFullQuantity > 0) {
                parts.push(`${result.underturfFullQuantity} × 1m³`);
              }

              if (result.underturfThirdQuantity > 0) {
                parts.push(`${result.underturfThirdQuantity} × ⅓m³`);
              }

              orderBreakdown.textContent = parts.length
                ? `Suggested combination: ${parts.join(' + ')}`
                : '';
              orderBreakdown.hidden = !parts.length;
            } else {
              orderBreakdown.textContent = '';
              orderBreakdown.hidden = true;
            }
          }
        });

        if (this.externalResults.length && this.fallbackResult) {
          this.fallbackResult.hidden = true;
        }
      }

      setText(parent, selector, value) {
        const element = parent.querySelector(selector);
        if (element) element.textContent = value;
      }

      setResultsEmpty() {
        const panels = this.externalResults.length
          ? this.externalResults
          : this.fallbackResult
            ? [this.fallbackResult]
            : [];

        panels.forEach((panel) => {
          if (panel === this.fallbackResult && this.externalResults.length) {
            panel.hidden = true;
            return;
          }

          panel.hidden = false;
          panel.querySelector('[data-result-empty]')?.removeAttribute('hidden');
          panel.querySelector('[data-result-complete]')?.setAttribute('hidden', '');
        });
      }

      reset() {
        this.form.reset();

        this.getAreas().slice(1).forEach((area) => area.remove());
        const firstArea = this.getAreas()[0];
        if (firstArea) {
          firstArea.querySelectorAll('input').forEach((input) => {
            if (!input.matches('[type="checkbox"]')) input.value = '';
          });
        }

        if (this.depthInput) this.depthInput.value = this.defaultDepth;
        if (this.wastageInput) this.wastageInput.value = this.defaultWastage;
        if (this.wastageCheckbox) this.wastageCheckbox.checked = this.defaultWastageEnabled;

        this.renumberAreas();
        this.syncAreaUI();
        this.updateWastageUI();
        this.hideError();
        this.setResultsEmpty();
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = false;
      }

      hideError() {
        if (!this.error) return;
        this.error.textContent = '';
        this.error.hidden = true;
      }
    }
  );
}